import { useQuery } from "@tanstack/react-query";
import { useApiClient, type ApiClientConfig } from "@playbookai/api-clients";
import { kpiApi } from "../api/live-client.js";
import type { ManagedKpiObservation } from "../api/types.js";
import { kpiKeys } from "./kpiQueryKeys.js";

/** The ranges the detail page offers. `all` sends no `from` bound. */
export const KPI_OBSERVATION_RANGES = ["30d", "90d", "all"] as const;
export type ManagedKpiObservationRange =
  (typeof KPI_OBSERVATION_RANGES)[number];

export const KPI_OBSERVATION_RANGE_LABEL: Record<
  ManagedKpiObservationRange,
  string
> = {
  "30d": "30 days",
  "90d": "90 days",
  all: "All",
};

const DAY_MS = 86_400_000;
const RANGE_DAYS: Record<ManagedKpiObservationRange, number | null> = {
  "30d": 30,
  "90d": 90,
  all: null,
};

/**
 * The server pages at 100. A chart wants the whole window, so the hook sweeps
 * the cursor — bounded, so a bad server cursor cannot spin and an unexpectedly
 * enormous history cannot lock the tab up assembling it.
 *
 * 20 pages is 2,000 readings: about five and a half years of daily reporting,
 * comfortably past the widest range this page offers. Hitting the cap THROWS
 * rather than returning a partial series, for the same reason `listDefinitions`
 * does — a chart silently missing its oldest half is worse than a visible error,
 * because nothing on screen says it is incomplete.
 */
const MAX_PAGES = 20;
const PAGE_LIMIT = 100;

async function sweep(
  config: ApiClientConfig,
  kpiId: string,
  from: string | undefined,
  signal: AbortSignal | undefined,
): Promise<ManagedKpiObservation[]> {
  const rows: ManagedKpiObservation[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page++) {
    // The query's AbortSignal rides on EVERY page: a sweep is one logical
    // request, and cancelling the query (unmount, range change) must stop the
    // remaining pages, not just orphan them.
    const res = await kpiApi.listObservations(
      config,
      kpiId,
      {
        ...(from ? { from } : {}),
        limit: PAGE_LIMIT,
        ...(cursor ? { cursor } : {}),
      },
      signal,
    );
    rows.push(...res.data);
    if (!res.hasMore) return rows;
    if (!res.nextCursor) {
      throw new Error(
        "KPI observation pagination indicated more results without a next cursor.",
      );
    }
    cursor = res.nextCursor;
  }
  throw new Error(
    `KPI observation history exceeded ${MAX_PAGES} pages; refusing to draw a partial series.`,
  );
}

/**
 * A KPI's observation history for one range, oldest-first.
 *
 * The server returns newest-first (its keyset descends on `observedAt`); the
 * chart needs ascending, so the reversal happens ONCE here rather than in every
 * consumer. Everything downstream can then assume "ascending by `observedAt`".
 *
 * The range is resolved to a `from` instant when the query RUNS, not when the
 * key is built — see `kpiKeys.observationRange`. A key carrying the instant
 * would change on every render and refetch the whole series each time.
 */
export function useKpiObservations(
  kpiId: string | undefined,
  range: ManagedKpiObservationRange,
) {
  const config = useApiClient();
  return useQuery<ManagedKpiObservation[]>({
    queryKey: kpiKeys.observationRange(kpiId ?? "", range),
    queryFn: async ({ signal }) => {
      const days = RANGE_DAYS[range];
      const from =
        days === null
          ? undefined
          : new Date(Date.now() - days * DAY_MS).toISOString();
      const rows = await sweep(config, kpiId!, from, signal);
      return rows.reverse();
    },
    enabled: Boolean(kpiId),
    staleTime: 30_000,
    retry: false,
  });
}
